import { styled } from '@mui/material/styles';
import { Box, Typography, Container, TextField, InputAdornment, Chip } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import SportsEsportsIcon from '@mui/icons-material/SportsEsports';
import SportsIcon from '@mui/icons-material/Sports';
import ChildCareIcon from '@mui/icons-material/ChildCare';
import VerifiedIcon from '@mui/icons-material/Verified';
import LockIcon from '@mui/icons-material/Lock';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import EventAvailableIcon from '@mui/icons-material/EventAvailable';
import {
  motion,
  useInView,
  useMotionValue,
  useTransform,
  animate,
  useReducedMotion,
} from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { useState, useRef, useEffect } from 'react';
import type { VenueTypeCode } from '../constants/venueTypes';
import { VenueType } from '../constants/venueTypes';
import { fadeUp, staggerContainer, reducedMotionVariants } from '../../../theme/animations';
import { venueTypeColors } from '../../../theme/venueTypeColors';
import { usePlatformStats } from '../hooks/usePlatformStats';
import { useLandingAnalytics } from '../hooks/useLandingAnalytics';
import { useCity } from '../../../contexts/CityContext';
import { HeroScene } from '../../../components/ui/illustrations/venue-themed';

const HeroContainer = styled(Box)(({ theme }) => ({
  position: 'relative',
  minHeight: '90vh',
  display: 'flex',
  alignItems: 'center',
  background:
    theme.palette.mode === 'dark'
      ? 'linear-gradient(180deg, #0A0A0A 0%, #1A1A1A 100%)'
      : 'linear-gradient(180deg, #FAFAF8 0%, #F5F5F3 100%)',
  overflow: 'hidden',
  paddingTop: theme.spacing(10),
  paddingBottom: theme.spacing(6),
  [theme.breakpoints.down('md')]: {
    minHeight: 'auto',
    paddingTop: theme.spacing(12),
    paddingBottom: theme.spacing(6),
  },
}));

const ContentWrapper = styled(Container)({
  position: 'relative',
  zIndex: 1,
  textAlign: 'center',
});

const Headline = styled(Typography)(({ theme }) => ({
  fontFamily: theme.typography.fontFamilyMono,
  fontWeight: 700,
  fontSize: '3.5rem',
  lineHeight: 1.15,
  color: theme.palette.text.primary,
  marginBottom: theme.spacing(2),
  [theme.breakpoints.down('md')]: {
    fontSize: '2.25rem',
  },
  [theme.breakpoints.down('sm')]: {
    fontSize: '1.75rem',
  },
}));

const Subheadline = styled(Typography)(({ theme }) => ({
  fontFamily: theme.typography.fontFamily,
  fontSize: '1.125rem',
  color: theme.palette.text.secondary,
  maxWidth: 540,
  margin: '0 auto',
  marginBottom: theme.spacing(5),
  lineHeight: 1.6,
  [theme.breakpoints.down('md')]: {
    fontSize: '1rem',
    marginBottom: theme.spacing(4),
  },
}));

const SearchContainer = styled(Box)(({ theme }) => ({
  maxWidth: 560,
  margin: '0 auto',
  marginBottom: theme.spacing(4),
}));

const StyledTextField = styled(TextField)(({ theme }) => ({
  '& .MuiOutlinedInput-root': {
    backgroundColor: theme.palette.background.paper,
    borderRadius: 16,
    fontSize: '1rem',
    fontFamily: theme.typography.fontFamily,
    boxShadow: '0 4px 24px rgba(0, 0, 0, 0.06)',
    '& fieldset': {
      borderColor: theme.palette.divider,
      borderWidth: 1.5,
    },
    '&:hover fieldset': {
      borderColor: theme.palette.primary.main,
    },
    '&.Mui-focused fieldset': {
      borderColor: theme.palette.primary.main,
      borderWidth: 2,
    },
  },
}));

const CategoryPills = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'center',
  gap: theme.spacing(1.5),
  flexWrap: 'wrap',
  marginBottom: theme.spacing(5),
}));

const CategoryChip = styled(Chip, {
  shouldForwardProp: (prop) => prop !== 'venueColor',
})<{ venueColor: string }>(({ theme, venueColor }) => ({
  fontFamily: theme.typography.fontFamily,
  fontWeight: 500,
  fontSize: '0.875rem',
  padding: '6px 12px',
  height: 'auto',
  borderRadius: 9999,
  backgroundColor: `${venueColor}15`,
  color: venueColor,
  border: `1.5px solid ${venueColor}30`,
  cursor: 'pointer',
  transition: 'all 0.2s ease',
  '& .MuiChip-icon': {
    color: venueColor,
    marginLeft: 4,
  },
  '&:hover': {
    backgroundColor: `${venueColor}25`,
    borderColor: venueColor,
    transform: 'translateY(-2px)',
  },
}));

const SocialProofStrip = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'center',
  gap: theme.spacing(4),
  flexWrap: 'wrap',
  padding: theme.spacing(2, 0),
  [theme.breakpoints.down('sm')]: {
    gap: theme.spacing(2),
  },
}));

const ProofItem = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(0.75),
}));

const ProofNumber = styled(Typography)(({ theme }) => ({
  fontFamily: theme.typography.fontFamilyMono,
  fontWeight: 700,
  fontSize: '1rem',
  color: theme.palette.text.primary,
}));

const ProofLabel = styled(Typography)(({ theme }) => ({
  fontFamily: theme.typography.fontFamily,
  fontSize: '0.875rem',
  color: theme.palette.text.secondary,
}));

const TrustBadgesStrip = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'center',
  gap: theme.spacing(3),
  flexWrap: 'wrap',
  padding: theme.spacing(2, 0),
  marginTop: theme.spacing(1),
  [theme.breakpoints.down('sm')]: {
    gap: theme.spacing(1.5),
  },
}));

const TrustBadge = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(0.5),
  '& .MuiSvgIcon-root': {
    fontSize: '1rem',
    color: theme.palette.text.secondary,
  },
}));

const TrustLabel = styled(Typography)(({ theme }) => ({
  fontFamily: theme.typography.fontFamily,
  fontSize: '0.75rem',
  color: theme.palette.text.secondary,
}));

function AnimatedCounter({ value, suffix = '' }: { value: number; suffix?: string }) {
  const ref = useRef<HTMLSpanElement>(null);
  const isInView = useInView(ref, { once: true });
  const count = useMotionValue(0);
  const rounded = useTransform(count, (v) => Math.round(v));
  const [displayValue, setDisplayValue] = useState(0);

  useEffect(() => {
    if (isInView) {
      void animate(count, value, { duration: 1.5, ease: 'easeOut' });
    }
  }, [isInView, value, count]);

  useEffect(() => {
    const unsubscribe = rounded.on('change', (v) => setDisplayValue(v));
    return unsubscribe;
  }, [rounded]);

  return (
    <span ref={ref}>
      {isInView ? displayValue : 0}
      {suffix}
    </span>
  );
}

const categories = [
  { type: VenueType.GAMING, label: 'Gaming Venues', icon: <SportsEsportsIcon fontSize="small" /> },
  { type: VenueType.SNOOKER, label: 'Snooker Halls', icon: <SportsIcon fontSize="small" /> },
  {
    type: VenueType.KIDS_PLAYAREA,
    label: 'Kids Play Areas',
    icon: <ChildCareIcon fontSize="small" />,
  },
];

const trustBadges = [
  { icon: <VerifiedIcon />, label: 'Verified Venues' },
  { icon: <LockIcon />, label: 'Secure Payments' },
  { icon: <CheckCircleIcon />, label: 'Instant Confirmation' },
  { icon: <EventAvailableIcon />, label: 'Free Cancellation' },
];

export function HeroSection() {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const { data: stats } = usePlatformStats();
  const { selectedCity } = useCity();
  const { track } = useLandingAnalytics();
  const shouldReduceMotion = useReducedMotion();
  const containerVariants = shouldReduceMotion ? reducedMotionVariants : staggerContainer;
  const itemVariants = shouldReduceMotion ? reducedMotionVariants : fadeUp;

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      track({ action: 'hero_search', query: searchQuery });
      navigate(`/venues?search=${encodeURIComponent(searchQuery)}`);
    }
  };

  const handleCategoryClick = (venueType: VenueTypeCode) => {
    track({ action: 'category_click', venueType });
    navigate(`/venues?type=${venueType}`);
  };

  const totalVenues = stats?.totalVenues ?? 0;
  const totalCities = stats?.totalCities ?? 0;
  const totalBookings = stats?.totalBookings ?? 0;
  const hasStats = totalVenues > 0 || totalCities > 0;

  const cityName = selectedCity?.name;

  return (
    <HeroContainer>
      <HeroScene />
      <ContentWrapper maxWidth="md">
        <motion.div variants={containerVariants} initial="hidden" animate="visible">
          <motion.div variants={itemVariants}>
            {/* eslint-disable-next-line @typescript-eslint/no-explicit-any */}
            <Headline {...({ component: 'h1' } as any)}>
              Gaming Arenas. Snooker Halls. Play Zones. Book Instantly.
            </Headline>
          </motion.div>

          <motion.div variants={itemVariants}>
            <Subheadline>
              {cityName
                ? `Discover and book the best venues in ${cityName} — verified spots, transparent pricing, instant confirmation.`
                : 'Discover and book the best venues near you — verified spots, transparent pricing, instant confirmation.'}
            </Subheadline>
          </motion.div>

          <motion.div variants={itemVariants}>
            <CategoryPills>
              {categories.map((category) => (
                <CategoryChip
                  key={category.type}
                  label={category.label}
                  icon={category.icon}
                  venueColor={venueTypeColors[category.type].primary}
                  onClick={() => handleCategoryClick(category.type)}
                />
              ))}
            </CategoryPills>
          </motion.div>

          <motion.div variants={itemVariants}>
            <SearchContainer>
              <form onSubmit={handleSearch}>
                <StyledTextField
                  fullWidth
                  placeholder={
                    cityName ? `Search venues in ${cityName}...` : 'Search venues, locations...'
                  }
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon sx={{ color: 'text.secondary' }} />
                      </InputAdornment>
                    ),
                  }}
                  sx={{ '& input': { py: 2 } }}
                />
              </form>
            </SearchContainer>
          </motion.div>

          {hasStats && (
            <motion.div variants={itemVariants}>
              <SocialProofStrip>
                {totalVenues > 0 && (
                  <ProofItem>
                    <ProofNumber>
                      <AnimatedCounter value={totalVenues} suffix="+" />
                    </ProofNumber>
                    <ProofLabel>Verified Venues</ProofLabel>
                  </ProofItem>
                )}
                {totalCities > 0 && (
                  <ProofItem>
                    <ProofNumber>
                      <AnimatedCounter value={totalCities} />
                    </ProofNumber>
                    <ProofLabel>Cities</ProofLabel>
                  </ProofItem>
                )}
                {totalBookings > 0 && (
                  <ProofItem>
                    <ProofNumber>
                      <AnimatedCounter value={totalBookings} suffix="+" />
                    </ProofNumber>
                    <ProofLabel>Bookings</ProofLabel>
                  </ProofItem>
                )}
              </SocialProofStrip>
            </motion.div>
          )}

          <motion.div variants={itemVariants}>
            <TrustBadgesStrip>
              {trustBadges.map((badge) => (
                <TrustBadge key={badge.label}>
                  {badge.icon}
                  <TrustLabel>{badge.label}</TrustLabel>
                </TrustBadge>
              ))}
            </TrustBadgesStrip>
          </motion.div>
        </motion.div>
      </ContentWrapper>
    </HeroContainer>
  );
}
